import { useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Loader2, RefreshCw } from "lucide-react";
import { formatDMY } from "@/lib/date";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AvailabilityTimeline } from "@/components/AvailabilityTimeline";

const toDateObj = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const AvailabilitySection = ({ selectedDate, onDateChange, availability, isLoading, onRefresh }) => {
  const [open, setOpen] = useState(false);

  const handleSelect = (date) => {
    if (!date) return;
    onDateChange(toDateStr(date));
    setOpen(false);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6"
      data-testid="availability-section"
    >
      <div className="absolute left-0 right-0 top-0 h-2 bg-[#0F172A]" aria-hidden="true" />

      <div className="mb-4 flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-[#5C2D91]" />
        <h2 className="font-heading text-lg font-semibold text-[#0F172A] sm:text-xl">
          Availability Overview
        </h2>
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            data-testid="availability-refresh"
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-[#0F172A] transition-all hover:border-[#5C2D91] hover:text-[#5C2D91] disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        )}
      </div>

      <div className="mb-4">
        <label className="mb-1.5 block text-sm font-semibold tracking-wide text-[#0F172A]">
          Select date
        </label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="availability-date-trigger"
              className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-left text-base text-[#0F172A] outline-none transition-all hover:border-[#5C2D91] focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91] sm:max-w-xs"
            >
              <span data-testid="availability-date-value">{formatDMY(selectedDate) || "Choose a date"}</span>
              <CalendarDays className="h-4 w-4 shrink-0 text-[#5C2D91]" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-auto rounded-xl border-slate-200 p-0 shadow-lg"
            data-testid="availability-calendar-popover"
          >
            <Calendar
              mode="single"
              selected={toDateObj(selectedDate)}
              onSelect={handleSelect}
              defaultMonth={toDateObj(selectedDate)}
              initialFocus
              classNames={{
                day_today: "ring-2 ring-[#5C2D91] ring-offset-1 rounded-md font-bold text-[#5C2D91]",
                day_selected:
                  "bg-[#5C2D91] text-white hover:bg-[#4A2475] hover:text-white focus:bg-[#5C2D91] focus:text-white rounded-md",
                day: "h-9 w-9 p-0 font-normal rounded-md text-[#0F172A] transition-colors hover:bg-[#5C2D91]/10 aria-selected:opacity-100",
                caption_label: "text-sm font-semibold text-[#0F172A]",
                head_cell: "text-[#64748B] rounded-md w-9 font-medium text-[0.75rem]",
                day_disabled: "text-slate-300 opacity-60 hover:bg-transparent cursor-not-allowed",
              }}
            />
          </PopoverContent>
        </Popover>

        <div className="mt-1.5 flex items-center gap-2">
          {selectedDate && (
            <p className="text-sm font-semibold text-[#0F172A]" data-testid="availability-date-display">
              Showing: {formatDMY(selectedDate)}
            </p>
          )}
          {isLoading && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#5C2D91]" data-testid="availability-loading">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-[#64748B]">
          Booked slots and their 15-minute handover buffers are blocked and cannot be re-booked.
        </p>
      </div>

      {isLoading && availability.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-[#64748B]" data-testid="availability-list">
          <Loader2 className="h-4 w-4 animate-spin text-[#5C2D91]" /> Loading availability…
        </div>
      ) : (
        <AvailabilityTimeline availability={availability} selectedDate={selectedDate} />
      )}
    </motion.section>
  );
};
